// ============================================================================
// STUDIO CANETTE — NOTIFICATIONS
//
// Une génération dure deux bonnes minutes et coûte un crédit : l'utilisateur
// doit savoir immédiatement ce qui s'est passé, sans qu'on lui vole le focus avec
// une modale. Le toast est la seule voie de retour non bloquante de
// l'application — les pages n'écrivent pas de messages d'erreur dans leur
// markup à la main.
//
// `toastError` traduit les codes métier renvoyés par l'API (`insufficient_credits`,
// `feature_locked`…) avec les mots de shared/copy.js : le même incident doit
// produire exactement la même phrase, qu'il vienne du studio ou de la page
// Compte.
// ============================================================================

import { h, on } from './dom.js';
import { ERRORS } from '../../shared/copy.js';

/** Au-delà, la pile masque la page : la plus ancienne s'efface. */
const MAX_VISIBLE = 4;

/** Icônes : du texte, pas des SVG — un toast doit apparaître sans rien charger. */
const GLYPH = { info: 'i', ok: '✓', error: '!', warn: '▲' };

const stack = [];

function host() {
  let n = document.getElementById('toasts');
  if (!n) {
    n = h('div#toasts.toasts', { 'aria-live': 'polite' });
    document.body.appendChild(n);
  }
  return n;
}

/**
 * Affiche une notification.
 *
 * @param {string} message                     texte brut (jamais du HTML)
 * @param {Object} [opts]
 * @param {'info'|'ok'|'error'|'warn'} [opts.type]
 * @param {number} [opts.timeout]              0 = persistant jusqu'au clic
 * @param {{label:string, onClick:Function}} [opts.action]
 * @returns {() => void} ferme le toast par programme
 */
export function toast(message, { type = 'info', timeout = 4200, action = null } = {}) {
  const box = host();
  let timer = null, closed = false;

  const close = () => {
    if (closed) return;
    closed = true;
    clearTimeout(timer);
    const i = stack.indexOf(node);
    if (i >= 0) stack.splice(i, 1);
    node.classList.add('is-out');
    // On retire après l'animation de sortie ; si l'utilisateur a désactivé les
    // animations, tokens.css les ramène à 1 ms et le délai ne se voit pas.
    setTimeout(() => node.remove(), 220);
  };

  const arm = () => {
    if (!timeout) return;
    clearTimeout(timer);
    timer = setTimeout(close, timeout);
  };

  const node = h('div.toast', {
    class: `toast--${type}`,
    role: type === 'error' ? 'alert' : 'status',
    dataset: { type }
  },
    h('span.toast__icon', { 'aria-hidden': 'true', text: GLYPH[type] || GLYPH.info }),
    h('p.toast__msg', { text: String(message ?? '') }),
    action && action.label
      ? h('button.toast__action.btn.btn--sm', {
          type: 'button',
          text: action.label,
          onclick: () => { close(); try { action.onClick?.(); } catch (e) { console.error('[toast]', e); } }
        })
      : null,
    h('button.toast__close', {
      type: 'button', 'aria-label': 'Fermer', text: '×', onclick: close
    })
  );

  // Le compte à rebours se suspend au survol : on ne veut pas qu'un message
  // disparaisse sous le curseur de quelqu'un en train de le lire.
  on(node, 'pointerenter', () => clearTimeout(timer));
  on(node, 'pointerleave', arm);

  box.appendChild(node);
  stack.push(node);
  while (stack.length > MAX_VISIBLE) {
    const old = stack.shift();
    old.classList.add('is-out');
    setTimeout(() => old.remove(), 220);
  }
  arm();
  return close;
}

/** Raccourcis de lecture. */
export const toastOk = (m, o) => toast(m, { ...o, type: 'ok' });

/**
 * Affiche une erreur, en préférant toujours la formulation métier.
 *
 * Ordre de résolution : code connu (ERRORS) → message renvoyé par le serveur →
 * message de l'exception. Une erreur réseau ou un abandon volontaire ne
 * produisent pas le même texte : « rien n'a été facturé » est une information
 * qui compte quand chaque appel coûte un crédit.
 */
export const toastError = msgOrError => {
  if (!msgOrError) return;
  if (typeof msgOrError === 'string') return toast(msgOrError, { type: 'error', timeout: 7000 });

  const e = msgOrError;
  if (e.name === 'AbortError' || e.code === 'aborted') return;   // annulation voulue : pas un incident

  const known = e.code && ERRORS[e.code];
  const network = e.code === 'network' || /failed to fetch|networkerror|load failed/i.test(e.message || '');
  const message = known
    || (network ? ERRORS.network : null)
    || (e.code === 'timeout' ? "Le serveur met trop de temps à répondre. Rien n'a été facturé." : null)
    || e.message
    || 'Une erreur est survenue.';

  return toast(message, { type: 'error', timeout: 7000 });
};
